"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { needsInvoiceDetails } from "@/lib/utils/company-info";

export type CompanyFormData = {
  companyName: string;
  organizationNumber: string;
  invoiceEmail: string;
  invoiceReference: string;
  invoiceType: "pdf_invoice" | "e_invoice";
  peppolId: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  country: string;
};

const formFromProfile = (profile: any): CompanyFormData => ({
  companyName: profile?.company_name || "",
  organizationNumber: profile?.organization_number || "",
  invoiceEmail: profile?.invoice_email || "",
  invoiceReference: profile?.invoice_reference || "",
  invoiceType: profile?.invoice_type || "pdf_invoice",
  peppolId: profile?.peppol_id || "",
  addressLine1: profile?.address_line_1 || "",
  addressLine2: profile?.address_line_2 || "",
  city: profile?.city || "",
  postcode: profile?.postcode || "",
  country: profile?.country || "",
});

export type PartnerOption = {
  id: number;
  company_name: string;
  organization_number: string | null;
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  postcode: string;
  country: string;
  invoice_reference: string | null;
  commission_percentage: number;
};

/**
 * Editing a corporate booking customer's company profile from a staff
 * booking page (booking staff, super admin and bar staff): the form state
 * for EditCompanyModal, its validation, the save, and the activity log entry.
 */
export function useCompanyEdit({
  booking,
  venueId,
  bookingId,
  onSaved,
  canEditPartner = false,
}: {
  booking: any;
  venueId: string;
  bookingId: string;
  /** Called after a successful save, e.g. to reload the booking. */
  onSaved: () => void | Promise<void>;
  /**
   * Also lets staff set, change or clear the booking's partner from the same
   * form (super admin and booking staff; bar staff don't get this).
   */
  canEditPartner?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [companyFormData, setCompanyFormData] = useState<CompanyFormData>(
    formFromProfile(null),
  );
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [endCustomerName, setEndCustomerName] = useState("");

  // Load the venue's active partners the first time the form opens.
  useEffect(() => {
    if (!isOpen || !canEditPartner || partners.length > 0) return;
    let cancelled = false;
    fetch(`/api/staff/venues/${venueId}/partners`)
      .then((r) => r.json())
      .then((json) => {
        if (!cancelled && json?.success && Array.isArray(json.data)) {
          setPartners(json.data as PartnerOption[]);
        }
      })
      .catch(() => {
        // Non-fatal: the company fields can still be edited.
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, canEditPartner, venueId, partners.length]);

  /** Opens the form filled with the company profile as it is now. */
  const open = () => {
    setCompanyFormData(formFromProfile(booking?.customer?.company_profile));
    setPartnerId(booking?.partner_id ?? null);
    setEndCustomerName(booking?.end_customer_name || "");
    setIsOpen(true);
  };

  /**
   * Picks a partner (or none). Choosing a partner fills the company fields
   * with the partner's details, as the new-booking cart does; staff can
   * still edit them before saving.
   */
  const selectPartner = (id: number | null) => {
    setPartnerId(id);
    if (id === null) {
      setEndCustomerName("");
      return;
    }
    const p = partners.find((x) => x.id === id);
    if (!p) return;
    setCompanyFormData((prev) => ({
      ...prev,
      companyName: p.company_name,
      organizationNumber: p.organization_number ?? "",
      addressLine1: p.address_line_1,
      addressLine2: p.address_line_2 ?? "",
      city: p.city,
      postcode: p.postcode,
      country: p.country,
      invoiceReference: p.invoice_reference ?? prev.invoiceReference,
    }));
  };

  const close = () => setIsOpen(false);

  const save = async () => {
    // Company name and organization number are always required. The rest
    // is what isCompanyInfoComplete() enforces for invoicing — validate it
    // only when the booking is actually invoiced, otherwise staff can save
    // a profile the reminder task will still reject. A card-paying company
    // only needs its name and number on file. Ordered to match the form's
    // visual field order so the toast sequence is intuitive.
    if (!companyFormData.companyName.trim()) {
      toast.error("Please provide a company name");
      return;
    }

    if (!companyFormData.organizationNumber.trim()) {
      toast.error("Please provide an organization number");
      return;
    }

    const invoiceRequired = needsInvoiceDetails(booking?.payment_method);
    if (invoiceRequired && !companyFormData.addressLine1.trim()) {
      toast.error("Please provide an address");
      return;
    }

    if (invoiceRequired && !companyFormData.city.trim()) {
      toast.error("Please provide a city");
      return;
    }

    if (invoiceRequired && !companyFormData.postcode.trim()) {
      toast.error("Please provide a postcode");
      return;
    }

    if (invoiceRequired && !companyFormData.country.trim()) {
      toast.error("Please select a country");
      return;
    }

    if (invoiceRequired && !companyFormData.invoiceEmail.trim()) {
      toast.error("Please provide an invoice email");
      return;
    }

    if (invoiceRequired && !companyFormData.invoiceReference.trim()) {
      toast.error("Please provide an invoice reference");
      return;
    }

    try {
      const customerId = booking?.customer?.id;
      if (!customerId) {
        toast.error("Customer ID not found");
        return;
      }

      // Partner first: it is refused once the booking is invoiced, and the
      // company fields may have been filled from the partner just picked.
      const partnerChanged =
        canEditPartner &&
        ((booking?.partner_id ?? null) !== partnerId ||
          (booking?.end_customer_name || "") !==
            (partnerId !== null ? endCustomerName.trim() : ""));
      if (partnerChanged) {
        const partnerResponse = await fetch(
          `/api/staff/venues/${venueId}/bookings/${bookingId}/partner`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              partnerId,
              endCustomerName:
                partnerId !== null ? endCustomerName.trim() || null : null,
            }),
          },
        );
        if (!partnerResponse.ok) {
          const errorData = await partnerResponse.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to update partner");
        }
      }

      // Store old values for activity log
      const companyProfile = booking.customer?.company_profile;
      const oldCompanyName = companyProfile?.company_name || "Not set";
      const oldOrganizationNumber =
        companyProfile?.organization_number || "Not set";
      const oldInvoiceEmail = companyProfile?.invoice_email || "Not set";
      const oldInvoiceReference =
        companyProfile?.invoice_reference || "Not set";

      const response = await fetch(`/api/users/${customerId}/company`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name: companyFormData.companyName.trim(),
          organization_number:
            companyFormData.organizationNumber.trim() || null,
          invoice_email: companyFormData.invoiceEmail.trim(),
          invoice_reference: companyFormData.invoiceReference.trim(),
          invoice_type: companyFormData.invoiceType,
          peppol_id: companyFormData.peppolId.trim() || null,
          address_line_1: companyFormData.addressLine1.trim() || null,
          address_line_2: companyFormData.addressLine2.trim() || null,
          city: companyFormData.city.trim() || null,
          postcode: companyFormData.postcode.trim() || null,
          country: companyFormData.country.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to update company information",
        );
      }

      const result = await response.json();

      // Log activity for company info update
      const activityResponse = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}/activities`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            activity_detail: {
              type: "company_info_updated",
              changes: {
                company_name: {
                  old: oldCompanyName,
                  new: companyFormData.companyName.trim(),
                },
                organization_number: {
                  old: oldOrganizationNumber,
                  new: companyFormData.organizationNumber.trim() || "Not set",
                },
                invoice_email: {
                  old: oldInvoiceEmail,
                  new: companyFormData.invoiceEmail.trim() || "Not set",
                },
                invoice_reference: {
                  old: oldInvoiceReference,
                  new: companyFormData.invoiceReference.trim() || "Not set",
                },
              },
              updated_by: "Staff",
            },
          }),
        },
      );

      if (!activityResponse.ok) {
        console.error("Failed to log activity for company info update");
      }

      // Show appropriate message based on response
      if (result.warning) {
        toast.success(result.message, {
          description: result.warning,
        });
      } else {
        toast.success("Company information updated successfully");
      }

      await onSaved();
      setIsOpen(false);
    } catch (error) {
      console.error("Error updating company information:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update company information";
      toast.error(errorMessage);
    }
  };

  return {
    isOpen,
    open,
    close,
    companyFormData,
    setCompanyFormData,
    save,
    partners,
    partnerId,
    selectPartner,
    endCustomerName,
    setEndCustomerName,
    canEditPartner,
  };
}
